import React, { useState, useEffect, useRef } from 'react';
import { useLocation, Outlet, Link } from 'react-router-dom';
import {
  TrophyIcon, UserGroupIcon, AcademicCapIcon,
  Squares2X2Icon as DashboardIcon, 
  Bars3Icon, MagnifyingGlassIcon,
  ArrowLeftOnRectangleIcon, GlobeAltIcon,
  QuestionMarkCircleIcon, PhoneIcon 
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';

const UserLayout = () => {
  const { user, signOut } = useAuth();
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const navItems = [
    { to: '/dashboard', icon: DashboardIcon, label: 'Overview' },
    { to: '/dashboard/competitions', icon: GlobeAltIcon, label: 'Live Competitions' },
    { to: '/dashboard/my-competitions', icon: TrophyIcon, label: 'My Participation' },
    { to: '/dashboard/my-internships', icon: AcademicCapIcon, label: 'My Internships' },
    { to: '/dashboard/certificates', icon: AcademicCapIcon, label: 'Achievements' },
     { to: '/dashboard/team', icon: UserGroupIcon, label: 'Create Team' },
    { to: '/dashboard/faq', icon: QuestionMarkCircleIcon, label: 'Help Center' },
    { to: '/dashboard/contact', icon: PhoneIcon, label: 'Contact Support' }, 
    
  ];

  return (
    <div className="fixed inset-0 bg-[#050505] text-gray-100 flex overflow-hidden">
      {/* Mobile Sidebar Overlay */}
      <AnimatePresence>
        {isSidebarOpen && (
          <motion.div 
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-40 lg:hidden" 
            onClick={() => setSidebarOpen(false)} 
          />
        )}
      </AnimatePresence>

      {/* Sidebar Navigation */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-72 bg-[#0a0a0a] border-r border-white/5 transition-transform duration-300 lg:relative lg:translate-x-0 ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          
          {/* UPDATED UNIFIED LOGO SECTION */}
          <div className="p-8 border-b border-white/5">
            <div className="flex items-center space-x-3 group select-none">
              <div className="relative">
                {/* Animated Glow Effect */}
                <div className="absolute -inset-1 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl blur opacity-20 group-hover:opacity-60 transition duration-1000"></div>
                
                <div className="relative h-10 w-10 rounded-xl bg-[#050505] border border-white/10 flex items-center justify-center text-white font-black shadow-2xl transition-transform group-hover:scale-105">
                  <span className="text-transparent bg-clip-text bg-gradient-to-br from-white to-gray-500 italic">GZ</span>
                </div>
              </div>
              
              <div className="flex flex-col">
                <span className="text-sm font-black text-white tracking-tighter italic  leading-none">
                  GenZ<span className="text-indigo-500">Evaluation</span>
                </span>
                <span className="text-[7px] font-bold text-gray-600 tracking-[0.3em] uppercase italic mt-1">Dashboard</span>
              </div>
            </div>
          </div>

          <nav className="flex-1 p-6 space-y-1.5 overflow-y-auto custom-scrollbar">
            {navItems.map((item) => {
              const isActive = location.pathname === item.to;
              return (
                <Link key={item.label} to={item.to} onClick={() => setSidebarOpen(false)}
                  className={`flex items-center space-x-3 px-5 py-3.5 rounded-2xl transition-all group ${isActive ? 'bg-indigo-600 text-white shadow-xl shadow-indigo-600/20 italic' : 'text-gray-500 hover:bg-white/5 hover:text-white'}`}>
                  <item.icon className={`h-5 w-5 ${isActive ? 'text-white' : 'group-hover:text-indigo-400'}`} />
                  <span className="text-[10px] font-black uppercase tracking-widest">{item.label}</span>
                </Link>
              );
            })}
          </nav>

          <div className="p-6 border-t border-white/5">
            <button onClick={signOut} className="w-full flex items-center space-x-3 px-5 py-4 text-red-500 hover:bg-red-500/10 rounded-2xl transition-all font-black text-[10px] uppercase tracking-widest italic">
              <ArrowLeftOnRectangleIcon className="h-5 w-5" />
              <span>Terminate Session</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Viewport */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <header className="h-20 bg-[#050505]/80 backdrop-blur-md border-b border-white/5 px-6 lg:px-10 flex items-center justify-between shrink-0">
          <button onClick={() => setSidebarOpen(true)} className="lg:hidden text-gray-400 p-2"><Bars3Icon className="h-6 w-6" /></button>
          
          <div className="hidden md:flex items-center bg-white/5 border border-white/5 rounded-2xl px-5 py-2.5 w-96 group focus-within:border-indigo-500/50 transition-all">
            <MagnifyingGlassIcon className="h-4 w-4 text-gray-600 group-focus-within:text-indigo-400 transition-colors mr-3" />
            <input type="text" placeholder="SEARCH SYSTEM..." className="bg-transparent border-none focus:ring-0 w-full text-[10px] font-black tracking-widest italic outline-none text-white placeholder:text-gray-700 uppercase" />
          </div>

          <div className="flex items-center space-x-4 relative" ref={dropdownRef}>
            <div className="text-right hidden sm:block">
              <p className="text-[11px] font-black text-white italic uppercase tracking-tighter">{user?.name||  user?.full_name||'Authorized User'}</p>
              <p className="text-[8px] text-indigo-500 font-black uppercase tracking-[0.2em] mt-1">Status: Active</p>
            </div>
            <button onClick={() => setIsProfileOpen(!isProfileOpen)} className="h-10 w-10 rounded-2xl bg-indigo-600/10 border border-indigo-500/20 flex items-center justify-center text-indigo-500 font-black italic hover:bg-indigo-600 hover:text-white transition-all">
               {user?.full_name?.charAt(0) || 'U'}
            </button>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-6 lg:p-10 custom-scrollbar">
          <Outlet /> 
        </main>
      </div>
    </div>
  );
};

export default UserLayout;